import type { FarmState } from '../domain/model';
import { defaultState } from '../domain/defaults';
import { normalizeState } from '../domain/normalize';
import { compareFarmCopies, sameFarmValue } from '../domain/syncComparison';
import { authErrorMessage, createSupabaseBackend, isNetworkError } from './supabase';
import { roleCanEdit, resolveAppMode, SyncConflictError, type AppMode, type AppUser, type FarmBackend, type FarmMembership, type FarmRole, type RemoteSnapshot, type StorageLike } from './types';

export const PREVIEW_STATE_KEY = 'xp_react_pruebas_state_v1';
export type RepositoryStatus = 'loading' | 'ready' | 'offline' | 'syncing' | 'conflict' | 'signed-out' | 'setup' | 'error';
export interface RepositorySnapshot {
  mode: AppMode; state: FarmState; status: RepositoryStatus; session: AppUser | null;
  farm: FarmMembership | null; role: FarmRole | null; canEdit: boolean; pending: boolean;
  error: string; conflict: RemoteSnapshot | null;
}
interface CacheEnvelope { state: FarmState; revision: string | null; pending: boolean; userId: string; farm: FarmMembership }
export interface RepositoryOptions { mode?: AppMode; storage?: StorageLike; backend?: FarmBackend; initialState?: FarmState; online?: () => boolean }

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const cacheKey = (userId: string, farmId: string) => `xp_react_state_v1:${userId}:${farmId}`;
const membershipKey = (userId: string) => `xp_react_membership_v1:${userId}`;
const hasData = (state: FarmState) => Object.entries(state).some(([key, value]) => Array.isArray(value) && key !== 'tareasHechas' && value.length > 0);

export function createAppRepository(options: RepositoryOptions = {}) {
  const mode = options.mode || resolveAppMode(import.meta.env.VITE_APP_MODE);
  const storage = options.storage || globalThis.localStorage;
  const backend = options.backend || createSupabaseBackend(mode);
  const online = options.online || (() => globalThis.navigator?.onLine !== false);
  let snapshot: RepositorySnapshot = { mode, state: clone(options.initialState || defaultState()), status: 'loading', session: null, farm: null, role: null, canEdit: mode === 'preview', pending: false, error: '', conflict: null };
  let revision: string | null = null;
  let generation = 0;
  let editVersion = 0;
  let syncPromise: Promise<void> | null = null;
  let authUnsubscribe: (() => void) | undefined;
  let disposed = false;
  let recoverySequence = 0;
  const listeners = new Set<() => void>();
  const emit = (patch: Partial<RepositorySnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    snapshot.canEdit = ['ready','offline','syncing','conflict'].includes(snapshot.status) && (mode === 'preview' || (!!snapshot.session && !!snapshot.farm && roleCanEdit(snapshot.role)));
    if (!disposed) listeners.forEach(listener => listener());
  };
  const readJson = (key: string): unknown => {
    const value = storage.getItem(key);
    if (!value) return null;
    try { return JSON.parse(value) as unknown; }
    catch { throw new Error(`La copia local ${key} no se pudo leer. No se ha sobrescrito; conserva una copia antes de continuar.`); }
  };
  const writeCache = (state: FarmState, pending: boolean, newRevision = revision) => {
    if (mode === 'preview') { storage.setItem(PREVIEW_STATE_KEY, JSON.stringify(state)); return; }
    if (!snapshot.session || !snapshot.farm) throw new Error('Debes iniciar sesión y seleccionar una finca antes de guardar.');
    const envelope: CacheEnvelope = { state, pending, revision: newRevision, userId: snapshot.session.id, farm: snapshot.farm };
    storage.setItem(cacheKey(snapshot.session.id, snapshot.farm.id), JSON.stringify(envelope));
  };
  const readCache = (user: AppUser, farm: FarmMembership): CacheEnvelope | null => {
    const raw = readJson(cacheKey(user.id, farm.id)) as CacheEnvelope | null;
    if (!raw) return null;
    if (raw.userId !== user.id || raw.farm?.id !== farm.id) throw new Error('La caché local no pertenece a esta cuenta y finca.');
    return { ...raw, state: normalizeState(raw.state) };
  };
  const readLegacyCache = (user: AppUser, farm: FarmMembership): CacheEnvelope | null => {
    const legacyFarm = readJson('xp_finca_v1') as { id?: string } | null;
    if (legacyFarm?.id !== farm.id) return null;
    const raw = readJson('xp_state_v1');
    if (!raw) return null;
    return { state: normalizeState(raw), pending: storage.getItem('xp_pending_sync_v1') === 'true', revision: null, userId: user.id, farm };
  };
  const applyRemote = (remote: RemoteSnapshot | null) => {
    const state = remote ? normalizeState(remote.state) : defaultState();
    revision = remote?.revision || null;
    writeCache(state, false);
    emit({ state, pending: false, conflict: null, status: 'ready', error: '' });
  };

  // Reconcile only identical data or explicit print acknowledgements. Every
  // operational record (including unknown fields) must agree before rebasing.
  const reconcileRemote = (remote: RemoteSnapshot | null, allowPrint = true): boolean => {
    if (!remote || !snapshot.session || !snapshot.farm) return false;
    const comparison = compareFarmCopies(snapshot.state, remote.state);
    if (!comparison.equivalent && !(allowPrint && comparison.onlyPrintAcknowledgements && roleCanEdit(snapshot.role))) return false;
    const prefix = `${cacheKey(snapshot.session.id, snapshot.farm.id)}:before-reconcile:${Date.now()}:`;
    let key: string;
    do { key = prefix + ++recoverySequence; } while (storage.getItem(key) !== null);
    try {
      storage.setItem(key, JSON.stringify({ state: snapshot.state, remote, savedAt: new Date().toISOString() }));
    } catch (cause) {
      throw new Error('No se pudo guardar la copia de recuperación en este navegador. Se conservan los pendientes; descarga ambas copias antes de continuar.', { cause });
    }
    if (comparison.equivalent) applyRemote(remote);
    else {
      // The next write still uses compare-and-swap at this exact remote revision.
      revision = remote.revision;
      writeCache(snapshot.state, true);
      emit({ pending: true, conflict: null, status: 'ready', error: '' });
    }
    return true;
  };

  async function connectUser(user: AppUser) {
    const run = ++generation;
    revision = null;
    emit({ session: user, status: 'loading', error: '', conflict: null, farm: null, role: null, state: defaultState(), pending: false });
    let verifiedFarm: FarmMembership | null = null;
    try {
      const farm = await backend.getMembership(user.id);
      if (run !== generation || disposed) return;
      if (!farm) { emit({ farm: null, role: null, state: defaultState(), status: 'setup', pending: false }); return; }
      verifiedFarm = farm;
      storage.setItem(membershipKey(user.id), JSON.stringify(farm));
      emit({ farm, role: farm.role });
      const cache = readCache(user, farm) || readLegacyCache(user, farm);
      if (cache) { revision = cache.revision; emit({ state: cache.state, pending: cache.pending }); }
      const remote = await backend.getSnapshot(farm.id);
      if (run !== generation || disposed) return;
      if (cache?.pending || (cache && hasData(cache.state) && (!remote || !hasData(remote.state)))) {
        writeCache(cache.state, true, cache.revision);
        emit({ state: cache.state, pending: true });
        if (remote && remote.revision !== cache.revision) {
          if (reconcileRemote(remote)) { if (snapshot.pending) await retrySync(false); }
          else emit({ status: 'conflict', conflict: remote, error: 'Hay datos locales y una versión remota distinta. Ninguna copia fue reemplazada.' });
        } else {
          revision = remote?.revision || null;
          emit({ status: online() ? 'ready' : 'offline' });
          await retrySync();
        }
      } else applyRemote(remote);
    } catch (error) {
      if (run !== generation || disposed) return;
      const cachedFarm = readJson(membershipKey(user.id)) as FarmMembership | null;
      const cache = cachedFarm ? readCache(user, cachedFarm) : null;
      if (cache && (!online() || isNetworkError(error))) {
        revision = cache.revision;
        const permittedFarm = verifiedFarm || cachedFarm || cache.farm;
        emit({ state: cache.state, farm: permittedFarm, role: permittedFarm.role, pending: cache.pending, status: 'offline', error: authErrorMessage(error) });
      } else emit({ status: 'error', error: authErrorMessage(error), canEdit: false });
    }
  }

  async function initialize() {
    disposed = false;
    try {
      if (mode === 'preview') {
        const raw = readJson(PREVIEW_STATE_KEY) || readJson('xp_pruebas_state_v1');
        const state = raw ? normalizeState(raw) : clone(options.initialState || defaultState());
        writeCache(state, false);
        emit({ state, status: 'ready', pending: false, error: '' });
        return;
      }
      if (!authUnsubscribe) authUnsubscribe = backend.onAuthChange(user => {
        if (disposed) return;
        if (!user) { ++generation; emit({ session: null, farm: null, role: null, status: 'signed-out', state: defaultState(), pending: false, conflict: null }); }
        else if (snapshot.session?.id !== user.id) void connectUser(user);
      });
      let user: AppUser | null;
      try { user = online() ? await backend.getUser() : await backend.getCachedUser(); }
      catch (error) {
        if (online() && !isNetworkError(error)) throw error;
        user = await backend.getCachedUser();
      }
      if (!user) { emit({ status: 'signed-out', session: null }); return; }
      await connectUser(user);
    } catch (error) { emit({ status: 'error', error: authErrorMessage(error) }); }
  }

  async function signIn(username: string, password: string) {
    emit({ error: '' });
    try {
      const user = await backend.signIn(username, password);
      if (mode === 'preview') emit({ session: user });
      else await connectUser(user);
    } catch (error) { emit({ error: authErrorMessage(error) }); throw error; }
  }

  async function signOut() {
    ++generation;
    await backend.signOut();
    if (mode === 'preview') emit({ session: null });
    else emit({ session: null, farm: null, role: null, state: defaultState(), status: 'signed-out', pending: false, conflict: null });
  }

  async function retrySync(allowPrintReconcile = true): Promise<void> {
    if (mode === 'preview' || !snapshot.pending || snapshot.conflict) return;
    if (!backend.writable) { emit({ status: 'error', error: 'El backend no permite escrituras remotas.' }); return; }
    if (!snapshot.session || !snapshot.farm || !roleCanEdit(snapshot.role)) return;
    if (!online()) { emit({ status: 'offline' }); return; }
    if (syncPromise) {
      const waitingGeneration = generation;
      await syncPromise;
      if (!disposed && generation === waitingGeneration && snapshot.pending && !snapshot.conflict && snapshot.status !== 'offline' && snapshot.status !== 'error') await retrySync();
      return;
    }
    const user = snapshot.session, farm = snapshot.farm, run = generation;
    let retryAcknowledgements = false;
    syncPromise = (async () => {
      try {
        const membership = await backend.getMembership(user.id);
        if (run !== generation) return;
        if (!membership || membership.id !== farm.id || !roleCanEdit(membership.role)) {
          emit({ role: membership?.role || null, status: 'error', error: 'Ya no tienes permiso para editar esta finca. La copia local sigue guardada.' }); return;
        }
        storage.setItem(membershipKey(user.id), JSON.stringify(membership));
        emit({ farm: membership, role: membership.role, status: 'syncing', error: '' });
        while (snapshot.pending && run === generation && !disposed) {
          const sendingVersion = editVersion;
          const sending = clone(snapshot.state);
          const saved = await backend.writeSnapshot(farm.id, user.id, sending, revision);
          if (run !== generation) return;
          revision = saved.revision;
          const stillPending = sendingVersion !== editVersion;
          writeCache(snapshot.state, stillPending);
          emit({ pending: stillPending, status: stillPending ? 'syncing' : 'ready', error: '' });
        }
      } catch (error) {
        if (run !== generation) return;
        if (error instanceof SyncConflictError) {
          let remote: RemoteSnapshot | null = null;
          try { remote = await backend.getSnapshot(farm.id); } catch { /* local snapshot is already durable */ }
          if (run !== generation || disposed) return;
          try {
            if (reconcileRemote(remote, allowPrintReconcile)) retryAcknowledgements = snapshot.pending;
            else emit({ status: 'conflict', conflict: remote || { state: defaultState(), revision: '' }, error: error.message });
          } catch (recoveryError) {
            emit({ status: 'error', conflict: remote, error: recoveryError instanceof Error ? recoveryError.message : 'No se pudo conservar la copia de recuperación.' });
          }
        } else emit({ status: 'offline', error: authErrorMessage(error) });
      } finally { syncPromise = null; }
    })();
    await syncPromise;
    if (retryAcknowledgements && run === generation && !disposed) await retrySync(false);
  }

  async function save(value: FarmState, force = false) {
    if (!snapshot.canEdit) throw new Error('Tu rol es de consulta: no puedes modificar los registros.');
    const state = normalizeState(clone(value));
    if (!force && sameFarmValue(state, snapshot.state)) return;
    writeCache(state, mode === 'production');
    ++editVersion;
    emit({ state, pending: mode === 'production', error: '', ...(mode === 'preview' ? { status: 'ready' as const } : {}) });
    await retrySync();
  }

  async function refresh() {
    if (mode === 'preview') {
      const run = generation, version = editVersion;
      const user = snapshot.session || await backend.getUser();
      if (!user) throw new Error('Inicia sesión para cargar una copia de solo lectura.');
      const farm = await backend.getMembership(user.id);
      if (!farm) throw new Error('La cuenta no tiene una finca vinculada.');
      const remote = await backend.getSnapshot(farm.id);
      if (!remote) throw new Error('La finca todavía no tiene registros remotos.');
      if (run !== generation || disposed) return;
      if (version !== editVersion) throw new Error('Se guardaron cambios mientras se cargaba la copia. Tus cambios se conservaron; vuelve a solicitar la actualización.');
      const state = normalizeState(remote.state);
      storage.setItem(`${PREVIEW_STATE_KEY}:previous`, JSON.stringify(snapshot.state));
      writeCache(state, false);
      emit({ state, session: user, farm, role: farm.role, status: 'ready', error: '' });
      return;
    }
    if (snapshot.conflict && snapshot.session && snapshot.farm) {
      const run = generation;
      const remote = await backend.getSnapshot(snapshot.farm.id);
      if (run !== generation || disposed) return;
      if (reconcileRemote(remote)) { if (snapshot.pending) await retrySync(false); }
      else emit({ conflict: remote || snapshot.conflict, status: 'conflict', error: 'Las copias contienen diferencias. Revisa los registros antes de elegir; ninguna copia fue reemplazada.' });
      return;
    }
    if (snapshot.pending) { await retrySync(); return; }
    if (snapshot.session) await connectUser(snapshot.session);
  }

  async function createFarm(name = 'Xtreme Pork') {
    if (mode !== 'production' || !backend.writable) throw new Error('No se pueden crear fincas remotas desde pruebas.');
    if (!snapshot.session || snapshot.farm) throw new Error('Esta sesión no necesita crear una finca.');
    const run = generation;
    const farm = await backend.createFarm(snapshot.session.id, name);
    if (run !== generation || disposed || !snapshot.session) return;
    emit({ farm, role: farm.role, status: 'ready' });
    storage.setItem(membershipKey(snapshot.session.id), JSON.stringify(farm));
    revision = null;
    await save(snapshot.state, true);
  }

  async function resolveConflict(choice: 'remote' | 'local') {
    if (mode !== 'production' || !snapshot.conflict || !snapshot.farm || !snapshot.session) return;
    const run = generation;
    const version = editVersion;
    const latest = await backend.getSnapshot(snapshot.farm.id);
    if (run !== generation || disposed || !snapshot.farm || !snapshot.session || !snapshot.conflict) return;
    if (version !== editVersion) { emit({ error: 'La copia local cambió mientras se revisaba la elección. Revisa los cambios antes de confirmar otra vez.' }); return; }
    // Require another review if the remote changed while the conflict dialog was open.
    if ((latest?.revision || '') !== snapshot.conflict.revision) {
      emit({ conflict: latest || { state: defaultState(), revision: '' }, status: 'conflict', error: 'La versión remota volvió a cambiar. Revisa la copia nueva antes de decidir.' }); return;
    }
    if (!latest) throw new Error('La copia remota no está disponible. Se conserva la copia local; vuelve a revisar antes de elegir.');
    storage.setItem(`${cacheKey(snapshot.session.id, snapshot.farm.id)}:before-conflict`, JSON.stringify({ state: snapshot.state, remote: latest, savedAt: new Date().toISOString() }));
    if (choice === 'remote') { applyRemote(latest); return; }
    if (!roleCanEdit(snapshot.role)) throw new Error('Tu rol no permite reemplazar la versión remota.');
    revision = latest?.revision || null;
    emit({ conflict: null, pending: true, status: 'ready' });
    writeCache(snapshot.state, true);
    await retrySync();
  }

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    initialize, signIn, signOut, save, refresh, createFarm, retrySync, resolveConflict,
    setOffline: () => { if (mode === 'production') emit({ status: 'offline' }); },
    dispose: () => { disposed = true; ++generation; authUnsubscribe?.(); authUnsubscribe = undefined; listeners.clear(); },
  };
}
export type AppRepository = ReturnType<typeof createAppRepository>;
